import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCalendarAlt, FaTicketAlt, FaUserTie, FaUser, FaChevronRight, FaCheckCircle, FaTimes, FaExclamationTriangle, FaBuilding, FaUniversity, FaSave } from 'react-icons/fa';
import GlobalHeader from '../../../components/GlobalHeader';
import { API_BASE_URL } from '../../../config/api';
import { validateOrganizerForm, handleOrganizerUpdate } from './organizerFormFunctions.js';

function NewMenupage() {
  const nav = useNavigate();
  const [hoverOrganiser, setHoverOrganiser] = useState(false);
  const [hoverCustomer, setHoverCustomer] = useState(false);
  const [showOrganizerPopup, setShowOrganizerPopup] = useState(false);
  const [organizerMessage, setOrganizerMessage] = useState('');
  const [showCustomerPopup, setShowCustomerPopup] = useState(false);
  const [customerMessage, setCustomerMessage] = useState('');
  const [profileIncomplete, setProfileIncomplete] = useState(false);

  // Organizer profile form state
  const [showOrganizerForm, setShowOrganizerForm] = useState(false);
  const [organizerData, setOrganizerData] = useState({
    company: '',
    vat_no: '',
    is_npo: false,
    bank_name: '',
    bank_code: '',
    bank_account_name: '',
    bank_account_number: ''
  });
  const [organizerErrors, setOrganizerErrors] = useState({});
  const [isUpdatingOrganizer, setIsUpdatingOrganizer] = useState(false);
  const [organizerUpdateMessage, setOrganizerUpdateMessage] = useState('');
  const [bankOptions, setBankOptions] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const [bankFetchError, setBankFetchError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const [bankSelectSize, setBankSelectSize] = useState(1);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const loadBanks = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      setBanksLoading(true);
      setBankFetchError('');

      try {
        const response = await fetch(`${API_BASE_URL}/payments/banks`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();

        if (data.success && Array.isArray(data.banks)) {
          const sortedBanks = [...data.banks]
            .filter((bank) => bank?.name && bank?.code)
            .sort((a, b) => a.name.localeCompare(b.name));

          if (sortedBanks.length) {
            setBankOptions(sortedBanks);
          }
        } else if (data.message) {
          setBankFetchError(data.message);
        }
      } catch (error) {
        console.error('Error fetching bank list:', error);
        setBankFetchError('Unable to load bank list. Please try again later.');
      } finally {
        setBanksLoading(false);
      }
    };

    loadBanks();
  }, []);

  // Define styles as objects
  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    fontFamily: '"Poppins", "Arial", sans-serif',
    color: '#333',
    backgroundColor: '#F1F8FDFF'
  };

  const contentWrapperStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '15px 20px',
    width: '100%'
  };

  const sectionHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px'
  };

  const sectionTitleStyle = {
    fontSize: '24px',
    fontWeight: 600,
    color: '#333',
    margin: 0
  };

  const userOptionsSectionStyle = {
    marginTop: '0',
    paddingTop: '0'
  };

  const userOptionsStyle = {
    display: 'flex',
    justifyContent: 'center',
    gap: isMobile ? '4px' : '30px',
    flexWrap: 'wrap'
  };

  const optionCardBaseStyle = {
    width: '380px',
    minHeight: '320px',
    background: 'white',
    borderRadius: '16px',
    overflow: 'visible',
    boxShadow: '0 15px 35px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s ease',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
    borderTop: '5px solid transparent',
    marginTop: isMobile ? '8px' : '100px'
  };

  const organiserCardStyle = {
    ...optionCardBaseStyle,
    borderTopColor: '#f5c874',
    transform: hoverOrganiser ? 'translateY(-10px)' : 'none',
    boxShadow: hoverOrganiser ? '0 20px 40px rgba(0, 0, 0, 0.15)' : '0 15px 35px rgba(0, 0, 0, 0.08)'
  };

  const customerCardStyle = {
    ...optionCardBaseStyle,
    borderTopColor: '#f5c874',
    transform: hoverCustomer ? 'translateY(-10px)' : 'none',
    boxShadow: hoverCustomer ? '0 20px 40px rgba(0, 0, 0, 0.15)' : '0 15px 35px rgba(0, 0, 0, 0.08)'
  };

  const cardIconStyle = {
    width: '80px',
    height: '80px',
    background: '#fcf0d7',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px',
    color: 'black',
    margin: '-40px auto 0',
    boxShadow: '0 2px 10px rgba(252, 240, 215, 0.3)',
    border: '5px solid white',
    position: 'relative',
    zIndex: 10
  };

  const customerIconStyle = {
    ...cardIconStyle
  };

  const optionContentStyle = {
    padding: '30px 25px 25px 25px',
    textAlign: 'center'
  };

  const cardTitleStyle = {
    fontSize: '22px',
    fontWeight: 700,
    margin: '10px 0',
    color: '#333'
  };

  const cardDescStyle = {
    color: '#666',
    marginBottom: '15px',
    fontSize: '14px'
  };

  const featureListStyle = {
    listStyle: 'none',
    padding: 0,
    margin: '15px 0',
    textAlign: 'left'
  };

  const featureItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '10px',
    color: '#555',
    fontSize: '14px'
  };

  const enterButtonBaseStyle = {
    background: '#EFB034FF',
    color: '#ffffff',
    border: '1px solid #EFB034FF',
    padding: '16px 32px',
    borderRadius: '12px',
    fontWeight: 600,
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'background 0.2s ease, color 0.2s ease',
    marginTop: '15px',
    width: '100%',
    boxShadow: 'none'
  };

  const customerButtonStyle = {
    ...enterButtonBaseStyle
  };

  // New centered role title style
  const centeredRoleTitleStyle = {
    fontSize: '32px',
    fontWeight: 700,
    color: '#333',
    margin: '-10px 0 30px',
    textAlign: 'center',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  };

  // Style for underline below the title
  const titleUnderlineStyle = {
    width: '80px',
    height: '4px',
    background: 'linear-gradient(135deg, #EFB034FF, #f3c15a)',
    borderRadius: '2px',
    marginTop: '8px',
  };

  // Check customer profile completeness
  const checkCustomerProfile = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/shared/user-profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      console.log('🔍 Customer profile check result:', result);
      
      if (result.success && result.data) {
        const profile = result.data;
        const isProfileIncomplete = !profile.firstname || !profile.surname || 
                                  !profile.cellnumber || profile.cellnumber === '0000000000';
        console.log('📊 Profile validation:', {
          firstname: profile.firstname,
          surname: profile.surname,
          cellnumber: profile.cellnumber,
          isProfileIncomplete
        });
        setProfileIncomplete(isProfileIncomplete);
      }
    } catch (error) {
      console.error('Error checking customer profile:', error);
    }
  };

  // Check customer access before navigation
  const handleCustomerAccess = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      // If no token, redirect to login
      nav('/');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/shared/user-profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      
      if (result.success && result.data) {
        const profile = result.data;
        const isProfileIncomplete = !profile.firstname || !profile.surname || 
                                  !profile.cellnumber || profile.cellnumber === '0000000000' || profile.cellnumber === '000000000';
        
        if (isProfileIncomplete) {
          setCustomerMessage('Please fill in all profile information required to access the customer side.');
          setShowCustomerPopup(true);
        } else {
          // Profile is complete, allow access
          nav('/customerdash');
        }
      }
    } catch (error) {
      console.error('Error checking customer access:', error);
      // On error, allow access but log the issue
      nav('/customerdash');
    }
  };

  // Check profile on component mount
  useEffect(() => {
    checkCustomerProfile();
  }, []);

  // Check organizer access before navigation
  const handleOrganizerAccess = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      // If no token, redirect to login
      nav('/');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/shared/check-organizer-profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();

      if (result.canAccessOrganizer) {
        // User can access organizer dashboard
        nav('/organiser-dash');
      } else {
        // User needs to complete profile
        setOrganizerMessage('Please fill in all profile information required to access the organizer side.');
        setShowOrganizerPopup(true);
      }
    } catch (error) {
      console.error('Error checking organizer access:', error);
      // On error, allow access but log the issue
      nav('/organiser-dash');
    }
  };

  // Check profile on component mount
  useEffect(() => {
    checkCustomerProfile();
  }, []);
  // Wrapper functions for organizer form
  const validateOrganizerFormWrapper = () => {
    return validateOrganizerForm(organizerData, setOrganizerErrors);
  };

  const handleOrganizerUpdateWrapper = async () => {
    console.log('👆 [FRONTEND] Organizer form submit button clicked');
    console.log('📋 [FRONTEND] Current organizerData state:', {
      company: organizerData.company,
      vat_no: organizerData.vat_no,
      bank_name: organizerData.bank_name,
      bank_code: organizerData.bank_code,
      bank_account_name: organizerData.bank_account_name,
      bank_account_number: organizerData.bank_account_number ? '***' : ''
    });

    if (!validateOrganizerFormWrapper()) {
      console.warn('⚠️ [FRONTEND] Form validation failed, aborting submission');
      return;
    }

    console.log('✅ [FRONTEND] Form validation passed, calling handleOrganizerUpdate...');
    await handleOrganizerUpdate(
      organizerData,
      setIsUpdatingOrganizer,
      setOrganizerUpdateMessage,
      setOrganizerErrors,
      setShowOrganizerForm,
      setShowOrganizerPopup,
      nav
    );
  };


  return (
    <div style={containerStyle}>
      <GlobalHeader
        title="Choose Your Role"
        subtitle=""
        backTo="/"
        logoutTo="/"
        hideBackButton
        profileIncomplete={profileIncomplete}
      />

      {/* Main Content */}
      <div style={contentWrapperStyle}>
        {/* User Options */}
        <div style={{ ...userOptionsSectionStyle, marginTop: '0', borderTop: 'none' }}>
          <div style={userOptionsStyle}>
            <div
              style={organiserCardStyle}
              onClick={handleOrganizerAccess} 
              onMouseEnter={() => setHoverOrganiser(true)}
              onMouseLeave={() => setHoverOrganiser(false)}
            >
              <div style={cardIconStyle}>
                <FaUserTie />
              </div>
              <div style={optionContentStyle}>
                <h2 style={cardTitleStyle}>Organiser</h2>
                <p style={cardDescStyle}>Create and manage your events</p>
                <ul style={featureListStyle}>
                  <li style={featureItemStyle}><FaCalendarAlt style={{color: 'black'}} /> Create new events</li>
                  <li style={featureItemStyle}><FaTicketAlt style={{color: 'black'}} /> Manage ticket sales</li>
                </ul>
                <button style={enterButtonBaseStyle}>Enter</button>
              </div>
            </div>
            
            <div
              style={customerCardStyle}
              onClick={handleCustomerAccess}
              onMouseEnter={() => setHoverCustomer(true)}
              onMouseLeave={() => setHoverCustomer(false)}
            >
              <div style={customerIconStyle}>
                <FaUser />
              </div>
              <div style={optionContentStyle}>
                <h2 style={cardTitleStyle}>Customer</h2>
                <p style={cardDescStyle}>Browse and book event tickets</p>
                <ul style={featureListStyle}>
                  <li style={featureItemStyle}><FaTicketAlt style={{color: 'black'}} /> Purchase tickets</li>
                  <li style={featureItemStyle}><FaCalendarAlt style={{color: 'black'}} /> View upcoming events</li>
                </ul>
                <button style={customerButtonStyle}>Enter</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Organizer Access Popup */}
      {showOrganizerPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #ffffff, #f8f9fa)',
            borderRadius: '16px',
            padding: '20px',
            maxWidth: '600px',
            width: '85%',
            height: 'auto',
            maxHeight: '40vh',
            overflowY: 'auto',
            position: 'relative',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
            border: '3px solid #EFB034FF',
          }}>
            <div style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              zIndex: 2,
            }}>
              <button 
                className="access-popup-close"
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  color: '#666',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '50%',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => {setShowOrganizerPopup(false); setShowOrganizerForm(true);}}
              >
                <FaTimes />
              </button>
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '64px',
                color: '#ef4444',
                marginBottom: '16px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
                <FaTimes />
              </div>

              <h3 style={{
                fontSize: '24px',
                fontWeight: 700,
                color: '#ef4444',
                margin: '0 0 12px 0',
              }}>
                Organizer Access Required
              </h3>

              <p style={{
                fontSize: '16px',
                color: '#666',
                lineHeight: '1.5',
                margin: '0 0 24px 0',
              }}>
                {organizerMessage}
              </p>

              <button
                style={{
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  padding: '12px 32px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
                onClick={() => {setShowOrganizerPopup(false); setShowOrganizerForm(true);}}
              >
                Fill in Required
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Access Popup */}
      {showCustomerPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #ffffff, #f8f9fa)',
            borderRadius: '16px',
            padding: '20px',
            maxWidth: '600px',
            width: '85%',
            height: 'auto',
            maxHeight: '40vh',
            overflowY: 'auto',
            position: 'relative',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
            border: '3px solid #EFB034FF',
          }}>
            <div style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              zIndex: 2,
            }}>
              <button 
                className="access-popup-close"
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  color: '#666',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '50%',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => setShowCustomerPopup(false)}
              >
                <FaTimes />
              </button>
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '64px',
                color: '#ef4444',
                marginBottom: '16px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
                <FaExclamationTriangle />
              </div>

              <h3 style={{
                fontSize: '24px',
                fontWeight: 700,
                color: '#ef4444',
                margin: '0 0 12px 0',
              }}>
                Customer Access Required
              </h3>

              <p style={{
                fontSize: '16px',
                color: '#666',
                lineHeight: '1.5',
                margin: '0 0 24px 0',
              }}>
                {customerMessage}
              </p>

              <button
                style={{
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  padding: '12px 32px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
                onClick={() => setShowCustomerPopup(false)}
              >
                Fill in Required
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Organizer Profile Form Popup */}
      {showOrganizerForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '900px',
            width: '90%',
            height: 'auto',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative',
            border: '2px solid #EFB034FF',
          }}>
            <div style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              zIndex: 2,
            }}>
              <button 
                className="access-popup-close"
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  color: '#666',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '50%',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => setShowOrganizerForm(false)}
              >
                <FaTimes />
              </button>
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '40px',
                color: '#EFB034FF',
                marginBottom: '12px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
                <FaBuilding />
              </div>

              <h3 style={{
                fontSize: '24px',
                fontWeight: 700,
                color: '#EFB034FF',
                margin: '0 0 8px 0',
              }}>
                Complete Organizer Profile
              </h3>
              
              <p style={{
                fontSize: '14px',
                color: '#666',
                lineHeight: '1.5',
                margin: '0 0 16px 0',
              }}>
                Please fill in your business information to access organizer features
              </p>
            </div>

            {organizerErrors._general && (
              <div style={{
                background: '#fee2e2',
                border: '1px solid #ef4444',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '16px',
                color: '#dc2626',
                fontSize: '14px',
                fontWeight: 500,
                textAlign: 'left',
              }}>
                {organizerErrors._general}
              </div>
            )}

            <div style={{ textAlign: 'left', marginBottom: '12px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#333',
                    marginBottom: '6px',
                  }}>
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={organizerData.company}
                    onChange={(e) => {
                      setOrganizerData({...organizerData, company: e.target.value});
                      if (organizerErrors.company) {
                        setOrganizerErrors({...organizerErrors, company: ''});
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: organizerErrors.company ? '2px solid #dc3545' : '2px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'all 0.3s ease',
                      outline: 'none',
                    }}
                    onFocus={(e) => e.target.style.borderColor = organizerErrors.company ? '#dc3545' : '#EFB034FF'}
                    onBlur={(e) => e.target.style.borderColor = organizerErrors.company ? '#dc3545' : '#e0e0e0'}
                    placeholder="Enter your company name"
                  />
                  {organizerErrors.company && (
                    <div style={{
                      color: '#dc3545',
                      fontSize: '12px',
                      marginTop: '4px',
                      fontWeight: 500,
                    }}>
                      {organizerErrors.company}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#333',
                    marginBottom: '6px',
                  }}>
                    VAT Number
                  </label>
                  <input
                    type="text"
                    value={organizerData.vat_no}
                    onChange={(e) => {
                      setOrganizerData({...organizerData, vat_no: e.target.value});
                      if (organizerErrors.vat_no) {
                        setOrganizerErrors({...organizerErrors, vat_no: ''});
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: organizerErrors.vat_no ? '2px solid #dc3545' : '2px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'all 0.3s ease',
                      outline: 'none',
                    }}
                    onFocus={(e) => e.target.style.borderColor = organizerErrors.vat_no ? '#dc3545' : '#3a7bd5'}
                    onBlur={(e) => e.target.style.borderColor = organizerErrors.vat_no ? '#dc3545' : '#e0e0e0'}
                    placeholder="Enter your VAT number"
                  />
                  {organizerErrors.vat_no && (
                    <div style={{
                      color: '#dc3545',
                      fontSize: '12px',
                      marginTop: '4px',
                      fontWeight: 500,
                    }}>
                      {organizerErrors.vat_no}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                  fontSize: '14px',
                  color: '#333',
                  cursor: 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={organizerData.is_npo}
                    onChange={(e) => {
                      setOrganizerData({ ...organizerData, is_npo: e.target.checked });
                    }}
                    style={{
                      marginTop: '2px',
                      width: '16px',
                      height: '16px',
                      accentColor: '#EFB034FF',
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  />
                  <span>
                    <strong>This is a Non-Profit Organisation (NPO)</strong>
                    <br />
                    <span style={{ fontSize: '12px', color: '#666' }}>
                      No platform commission will be taken from this organisation's ticket sales.
                    </span>
                  </span>
                </label>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#333',
                  marginBottom: '6px',
                }}>
                  Bank Name
                </label>
                <select
                  size={bankSelectSize}
                  value={organizerData.bank_name}
                  onChange={(e) => {
                    const selectedBank = bankOptions.find(b => b.name === e.target.value);
                    setOrganizerData({
                      ...organizerData,
                      bank_name: e.target.value,
                      bank_code: selectedBank?.code ?? ''
                    });
                    if (organizerErrors.bank_name) {
                      setOrganizerErrors({ ...organizerErrors, bank_name: '' });
                    }
                    if (organizerErrors.bank_code) {
                      setOrganizerErrors({ ...organizerErrors, bank_code: '' });
                    }
                    setBankSelectSize(1);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: (organizerErrors.bank_name || organizerErrors.bank_code) ? '2px solid #dc3545' : '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.3s ease',
                    outline: 'none',
                    backgroundColor: 'white',
                    cursor: banksLoading ? 'wait' : 'pointer',
                  }}
                  disabled={isUpdatingOrganizer}
                  onFocus={(e) => {
                    e.target.style.borderColor = (organizerErrors.bank_name || organizerErrors.bank_code) ? '#dc3545' : '#EFB034FF';
                    setBankSelectSize(5);
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = (organizerErrors.bank_name || organizerErrors.bank_code) ? '#dc3545' : '#e0e0e0';
                    setBankSelectSize(1);
                  }}
                >
                  <option value="">
                    {banksLoading ? 'Loading banks…' : 'Select your bank'}
                  </option>
                  {bankOptions.map((bank) => (
                    <option key={bank.code ?? bank.name} value={bank.name}>
                      {bank.name}
                    </option>
                  ))}
                </select>
                {(organizerErrors.bank_name || organizerErrors.bank_code) && (
                  <div style={{
                    color: '#dc3545',
                    fontSize: '12px',
                    marginTop: '4px',
                    fontWeight: 500,
                  }}>
                    {organizerErrors.bank_name || organizerErrors.bank_code}
                  </div>
                )}
                {!organizerErrors.bank_name && bankFetchError && (
                  <div style={{
                    color: '#b58900',
                    fontSize: '12px',
                    marginTop: '4px',
                    fontWeight: 500,
                  }}>
                    {bankFetchError}
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#333',
                    marginBottom: '6px',
                  }}>
                    Bank Account Number
                  </label>
                  <input
                    type="text"
                    value={organizerData.bank_account_number}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\s/g, '');
                      setOrganizerData({...organizerData, bank_account_number: value});
                      if (organizerErrors.bank_account_number) {
                        setOrganizerErrors({...organizerErrors, bank_account_number: ''});
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: organizerErrors.bank_account_number ? '2px solid #dc3545' : '2px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'all 0.3s ease',
                      outline: 'none',
                    }}
                    onFocus={(e) => e.target.style.borderColor = organizerErrors.bank_account_number ? '#dc3545' : '#3a7bd5'}
                    onBlur={(e) => e.target.style.borderColor = organizerErrors.bank_account_number ? '#dc3545' : '#e0e0e0'}
                    placeholder="Enter bank account number"
                  />
                  {organizerErrors.bank_account_number && (
                    <div style={{
                      color: '#dc3545',
                      fontSize: '12px',
                      marginTop: '4px',
                      fontWeight: 500,
                    }}>
                      {organizerErrors.bank_account_number}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#333',
                    marginBottom: '6px',
                  }}>
                    Bank Account Name
                  </label>
                  <input
                    type="text"
                    value={organizerData.bank_account_name}
                    onChange={(e) => {
                      setOrganizerData({...organizerData, bank_account_name: e.target.value});
                      if (organizerErrors.bank_account_name) {
                        setOrganizerErrors({...organizerErrors, bank_account_name: ''});
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: organizerErrors.bank_account_name ? '2px solid #dc3545' : '2px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'all 0.3s ease',
                      outline: 'none',
                    }}
                    onFocus={(e) => e.target.style.borderColor = organizerErrors.bank_account_name ? '#dc3545' : '#3a7bd5'}
                    onBlur={(e) => e.target.style.borderColor = organizerErrors.bank_account_name ? '#dc3545' : '#e0e0e0'}
                    placeholder="Enter bank account name"
                  />
                  {organizerErrors.bank_account_name && (
                    <div style={{
                      color: '#dc3545',
                      fontSize: '12px',
                      marginTop: '4px',
                      fontWeight: 500,
                    }}>
                      {organizerErrors.bank_account_name}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                style={{
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
                onClick={() => setShowOrganizerForm(false)}
              >
                Cancel
              </button>

              <button
                style={{
                  background: '#EFB034FF',
                  color: '#ffffff',
                  border: '1px solid #EFB034FF',
                  padding: '12px 24px',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background 0.2s ease, color 0.2s ease',
                  boxShadow: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                onClick={handleOrganizerUpdateWrapper}
                disabled={isUpdatingOrganizer}
              >
                <FaSave />
                {isUpdatingOrganizer ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Organizer Status Popup */}
      {isUpdatingOrganizer && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000,
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            position: 'relative',
            border: '2px solid #EFB034FF',
          }}>
            <div style={{
              fontSize: '48px',
              color: '#EFB034FF',
              marginBottom: '16px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              <FaBuilding />
            </div>

            <h3 style={{
              fontSize: '20px',
              fontWeight: 700,
              color: '#EFB034FF',
              margin: '0 0 12px 0',
            }}>
              {organizerUpdateMessage.includes('Account ready') ? 'Account Ready' : 'Preparing Account'}
            </h3>
            
            <p style={{
              fontSize: '14px',
              color: '#666',
              lineHeight: '1.5',
              margin: '0 0 20px 0',
            }}>
              {organizerUpdateMessage}
            </p>

            {organizerUpdateMessage.includes('Account ready') && (
              <div style={{
                fontSize: '32px',
                color: '#28a745',
                marginBottom: '16px',
              }}>
                ✓
              </div>
            )}

            {organizerUpdateMessage.includes('Account error') && (
              <div style={{
                fontSize: '32px',
                color: '#dc3545',
                marginBottom: '16px',
              }}>
                ✗
              </div>
            )}

            {organizerUpdateMessage.includes('Account error') && (
              <button
                style={{
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
                onClick={() => {
                  setIsUpdatingOrganizer(false);
                  setOrganizerUpdateMessage('');
                }}
              >
                Close
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );

}

export default NewMenupage;










